import { adminCaptureException } from '@diamond/shared/utils';
import { ErrorComponent } from '@diamond/sol-admin/components';
import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary for Activity Detail page
 * Catches rendering errors and displays user-friendly error message
 * Provides retry functionality by reloading the page
 * Logs errors to Sentry for monitoring
 */
export class ActivityDetailErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      'Activity Detail Error Boundary caught error:',
      error,
      errorInfo
    );

    // Send error to Sentry for monitoring
    adminCaptureException(
      {
        message: error.message || 'Activity Detail Page Error',
        error: error.name || 'UnknownError',
        stack: error.stack || '',
        componentStack: errorInfo.componentStack || '',
      },
      (scope) => {
        scope.setTags({
          errorBoundary: 'ActivityDetailErrorBoundary',
          component: 'activity-detail-page',
          errorType: 'render-error',
        });
        scope.setContext('error', {
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
        });
        scope.setContext('errorInfo', {
          componentStack: errorInfo.componentStack,
        });
        scope.setLevel('error');
      }
    );
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorComponent
          message="Terjadi kesalahan saat memuat detail aktivitas"
          subtitle={
            this.state.error?.message ||
            'Silakan coba lagi atau hubungi tim support'
          }
          buttonTitle="Muat Ulang Halaman"
          action={this.handleReset}
          variant="section"
          showIllustration={true}
        />
      );
    }

    return this.props.children;
  }
}
